import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { gsap } from "gsap";
import ApiURL from "../../controllers/Api";

const ProductReport = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState("");
  const [availabilityFilter, setAvailabilityFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [discountFilter, setDiscountFilter] = useState("");
  const [filterVisible, setFilterVisible] = useState(false);

  const wrapRef = useRef(null);
  const filterPanelRef = useRef(null);
  const tableContainerRef = useRef(null);

  // GSAP entrance
  useLayoutEffect(() => {
    if (!wrapRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".pr-anim",
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 0.45, stagger: 0.06, ease: "power2.out" }
      );
    }, wrapRef);
    return () => ctx.revert();
  }, []);

  // Helper function to check availability
  const checkAvailability = (isAvailable) => {
    return isAvailable === 1 || isAvailable === "1" ? "Available" : "Unavailable";
  };

  // Fetch product report data
  const fetchProductReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${ApiURL}/getProductReport.php`);
      const data = await response.json();

      if (data?.status === "success" && Array.isArray(data?.products)) {
        setProducts(data.products);
        setFilteredProducts(data.products);
      } else {
        setProducts([]);
        setFilteredProducts([]);
        setError("Failed to fetch data.");
      }
    } catch (err) {
      console.error(err);
      setProducts([]);
      setFilteredProducts([]);
      setError("Error fetching data.");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filteredData = [...products];

    // Category
    if (categoryFilter.trim()) {
      const q = categoryFilter.trim().toLowerCase();
      filteredData = filteredData.filter((p) =>
        String(p.category_name || "").toLowerCase().includes(q)
      );
    }

    // Availability
    if (availabilityFilter) {
      filteredData = filteredData.filter(
        (p) => checkAvailability(p.is_available).toLowerCase() === availabilityFilter.toLowerCase()
      );
    }

    // Date range
    if (dateFrom) {
      const from = new Date(dateFrom);
      filteredData = filteredData.filter((p) => new Date(p.created_at) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      filteredData = filteredData.filter((p) => new Date(p.created_at) <= to);
    }

    // Discount
    if (discountFilter) {
      if (discountFilter === "applicable") {
        filteredData = filteredData.filter(
          (p) => Array.isArray(p.discount_prices) && p.discount_prices.length > 0 && Number(p.discount_prices[0]) > 0
        );
      } else if (discountFilter === "notApplicable") {
        filteredData = filteredData.filter(
          (p) =>
            !Array.isArray(p.discount_prices) ||
            p.discount_prices.length === 0 ||
            Number(p.discount_prices[0]) <= 0
        );
      }
    }

    setFilteredProducts(filteredData);
    setFilterVisible(false);
  };

  const resetFilters = () => {
    setCategoryFilter("");
    setAvailabilityFilter("");
    setDateFrom("");
    setDateTo("");
    setDiscountFilter("");
    setFilteredProducts(products);
    setFilterVisible(false);
  };

  // CSV Export (safer escaping)
  const exportToCSV = () => {
    const header = [
      "Serial No",
      "Product ID",
      "Category",
      "IGST",
      "CGST",
      "SGST",
      "Product Name",
      "Sizes",
      "Prices",
      "Availability",
      "Created At",
      "Discounted Info",
      "Discount Available",
    ];

    const escapeCell = (cell) => {
      const v = cell == null ? "" : String(cell);
      const escaped = v.replace(/"/g, '""');
      return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
    };

    const rows = filteredProducts.map((product, index) => {
      const hasDiscount =
        Array.isArray(product.discount_prices) &&
        product.discount_prices.length > 0 &&
        Number(product.discount_prices[0]) > 0;

      const discountInfo = hasDiscount
        ? product.discount_prices
            .map((price, i) => {
              const minQty = product.discount_min_quantities?.[i] ?? "N/A";
              return `Discount Price: ${price} Min Qty: ${minQty}`;
            })
            .join(" | ")
        : "No Discount";

      return [
        index + 1,
        product.product_id,
        product.category_name,
        product.igst,
        product.cgst,
        product.sgst,
        product.product_name,
        product.product_size,
        product.product_price,
        checkAvailability(product.is_available),
        product.created_at ? new Date(product.created_at).toLocaleDateString() : "",
        discountInfo,
        hasDiscount ? "Discount Applicable" : "No Discount",
      ].map(escapeCell);
    });

    const csvContent = [header.map(escapeCell).join(","), ...rows.map((r) => r.join(","))].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "product_report.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Horizontal scroll buttons (mobile + desktop)
  const scrollTableLeft = () => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollBy({ left: -320, behavior: "smooth" });
    }
  };
  const scrollTableRight = () => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollBy({ left: 320, behavior: "smooth" });
    }
  };

  useEffect(() => {
    fetchProductReport();

    // Close filter panel on outside click
    const handleClickOutside = (event) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target)) {
        setFilterVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-[#F7EFE7] p-6 font-[Kanit] text-[#6f5e57]">Loading products...</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7EFE7] p-4 font-[Kanit] sm:p-6">
        <div className="rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] p-6 shadow-[0_20px_55px_rgba(43,0,0,0.08)]">
          <div className="font-serif text-2xl font-medium text-[#2B0000]">Product Report</div>
          <div className="mt-2 text-sm text-red-600">{error}</div>
          <button
            type="button"
            onClick={fetchProductReport}
            className="mt-4 rounded-xl bg-[#2B0000] px-4 py-2.5 text-sm font-semibold text-[#f4dfae] shadow-md transition hover:bg-[#4A090B]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const count = filteredProducts?.length || 0;

  return (
    <div ref={wrapRef} className="product-report-luxury min-h-screen w-full overflow-x-hidden bg-[#F7EFE7] p-3 font-[Kanit] text-[#2B0000] sm:p-5 lg:p-7">
      <div className="pr-anim mx-auto max-w-[1700px] rounded-[26px] border border-[#d8c6b7] bg-[#fffaf5] p-4 shadow-[0_22px_60px_rgba(43,0,0,0.08)] sm:p-6 lg:p-8">
        {/* Header */}
        <div className="pr-anim flex flex-col gap-5 border-b border-[#e3d6cb] pb-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="font-serif text-2xl font-medium text-[#2B0000] sm:text-3xl">Product Report</div>
            <div className="mt-1 text-sm text-[#6f5e57]">
              {count} product{count === 1 ? "" : "s"} (filtered)
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={exportToCSV}
              className="rounded-xl bg-[#8f6f38] px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-[#765a2d]"
            >
              Export CSV
            </button>

            <button
              type="button"
              onClick={() => setFilterVisible((v) => !v)}
              className="rounded-xl bg-[#2B0000] px-4 py-2.5 text-sm font-semibold text-[#f4dfae] shadow-md transition hover:-translate-y-0.5 hover:bg-[#4A090B]"
            >
              Filters
            </button>

            <button
              type="button"
              onClick={fetchProductReport}
              className="rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-semibold text-[#2B0000] transition hover:bg-[#fff7ed]"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Filters dropdown/panel */}
        <div className="pr-anim relative mt-4">
          {filterVisible && (
            <div
              ref={filterPanelRef}
              className="absolute right-0 z-50 w-[min(92vw,480px)] rounded-[22px] border border-[#d8c6b7] bg-[linear-gradient(145deg,#fffaf5,#f3e7dc)] p-4 shadow-[0_24px_70px_rgba(43,0,0,0.16)] sm:p-5"
            >
              <div className="font-serif text-xl font-medium text-[#2B0000]">Filter Products</div>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {/* Category */}
                <div>
                  <label className="block text-sm font-semibold text-[#4a352f]">Category</label>
                  <input
                    type="text"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                    placeholder="Search by category"
                  />
                </div>

                {/* Availability */}
                <div>
                  <label className="block text-sm font-semibold text-[#4a352f]">Availability</label>
                  <select
                    value={availabilityFilter}
                    onChange={(e) => setAvailabilityFilter(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                  >
                    <option value="">All</option>
                    <option value="Available">Available</option>
                    <option value="Unavailable">Unavailable</option>
                  </select>
                </div>

                {/* Date From */}
                <div>
                  <label className="block text-sm font-semibold text-[#4a352f]">Date From</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                  />
                </div>

                {/* Date To */}
                <div>
                  <label className="block text-sm font-semibold text-[#4a352f]">Date To</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                  />
                </div>

                {/* Discount */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-semibold text-[#4a352f]">Discount</label>
                  <select
                    value={discountFilter}
                    onChange={(e) => setDiscountFilter(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                  >
                    <option value="">All</option>
                    <option value="applicable">Discount Applicable</option>
                    <option value="notApplicable">No Discount</option>
                  </select>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="rounded-xl border border-[#d6c4b5] bg-[#f1e6dc] px-4 py-2.5 text-sm font-semibold text-[#2B0000] transition hover:bg-[#e8d8ca]"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={applyFilters}
                  className="rounded-xl bg-[#2B0000] px-4 py-2.5 text-sm font-semibold text-[#f4dfae] shadow-md transition hover:-translate-y-0.5 hover:bg-[#4A090B]"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Horizontal scroll controls (VISIBLE on mobile too) */}
        <div className="pr-anim mt-6 flex items-center justify-between gap-3 rounded-[18px] border border-[#e2d4c8] bg-[#fbf3eb] p-3">
          <button
            type="button"
            onClick={scrollTableLeft}
            className="flex items-center justify-center rounded-xl border border-[#d6c4b5] bg-white px-3 py-2 text-sm font-semibold text-[#2B0000] transition-all duration-200 hover:bg-[#fff7ed] active:translate-y-[1px]"
            aria-label="Scroll table left"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="ml-2 hidden sm:inline">Scroll Left</span>
          </button>

          <div className="text-center text-xs font-medium text-[#75645d] sm:text-sm">
            Swipe or use buttons to scroll horizontally
          </div>

          <button
            type="button"
            onClick={scrollTableRight}
            className="flex items-center justify-center rounded-xl border border-[#d6c4b5] bg-white px-3 py-2 text-sm font-semibold text-[#2B0000] transition-all duration-200 hover:bg-[#fff7ed] active:translate-y-[1px]"
            aria-label="Scroll table right"
          >
            <span className="mr-2 hidden sm:inline">Scroll Right</span>
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Product Table (responsive, scrollable, sticky header) */}
        <div className="pr-anim mt-3">
          <div
            ref={tableContainerRef}
            className="max-h-[560px] overflow-x-auto overflow-y-auto rounded-[22px] border border-[#d8c6b7] bg-white shadow-[0_16px_42px_rgba(43,0,0,0.06)] scroll-smooth overscroll-x-contain"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "#8f6f38 #f4e8dd",
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-x",
            }}
          >
            <table className="w-full min-w-[1600px] text-left text-sm">
              <thead className="sticky top-0 z-10 bg-[#eee2d7] text-xs font-semibold uppercase tracking-[0.12em] text-[#4b352f]">
                <tr>
                  <th className="border-b px-4 py-3">SNo</th>
                  <th className="border-b px-4 py-3">Product ID</th>
                  <th className="border-b px-4 py-3">Category</th>
                  <th className="border-b px-4 py-3 text-right">IGST</th>
                  <th className="border-b px-4 py-3 text-right">CGST</th>
                  <th className="border-b px-4 py-3 text-right">SGST</th>
                  <th className="border-b px-4 py-3">Product Name</th>
                  <th className="border-b px-4 py-3">Sizes</th>
                  <th className="border-b px-4 py-3">Prices</th>
                  <th className="border-b px-4 py-3">Availability</th>
                  <th className="border-b px-4 py-3">Created</th>
                  <th className="border-b px-4 py-3">Discounted Info</th>
                  <th className="border-b px-4 py-3">Discount Available</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-[#eadfd5]">
                {filteredProducts.map((product, index) => {
                  const available = checkAvailability(product.is_available);
                  const hasDiscount =
                    Array.isArray(product.discount_prices) &&
                    product.discount_prices.length > 0 &&
                    Number(product.discount_prices[0]) > 0;

                  return (
                    <tr key={product.product_id ?? index} className="transition hover:bg-[#fff5ea]">
                      <td className="px-4 py-3 text-[#2B0000]">{index + 1}</td>
                      <td className="px-4 py-3 font-semibold text-[#2B0000]">{product.product_id}</td>
                      <td className="px-4 py-3 text-[#5d4b44]">{product.category_name}</td>
                      <td className="px-4 py-3 text-right text-[#2B0000]">{product.igst}</td>
                      <td className="px-4 py-3 text-right text-[#2B0000]">{product.cgst}</td>
                      <td className="px-4 py-3 text-right text-[#2B0000]">{product.sgst}</td>
                      <td className="px-4 py-3 text-[#2B0000]">{product.product_name}</td>
                      <td className="px-4 py-3 text-[#5d4b44]">{product.product_size}</td>
                      <td className="px-4 py-3 text-[#5d4b44]">{product.product_price}</td>

                      <td className="px-4 py-3">
                        <span
                          className={[
                            "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold",
                            available === "Available"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800",
                          ].join(" ")}
                        >
                          {available}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-[#5d4b44]">
                        {product.created_at ? new Date(product.created_at).toLocaleDateString() : ""}
                      </td>

                      <td className="px-4 py-3 text-[#5d4b44]">
                        {hasDiscount ? (
                          <div className="space-y-2">
                            {product.discount_prices.map((price, i) => (
                              <div key={i} className="rounded-lg border border-[#e1d3c8] bg-[#fffaf5] px-2.5 py-2 shadow-sm">
                                <div className="text-xs text-[#7d6c64]">Tier {i + 1}</div>
                                <div className="text-sm">
                                  <span className="font-semibold text-[#2B0000]">Discount:</span>{" "}
                                  {price}
                                </div>
                                <div className="text-sm">
                                  <span className="font-semibold text-[#2B0000]">Min Qty:</span>{" "}
                                  {product.discount_min_quantities?.[i] ?? "N/A"}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[#75645d]">No Discount</span>
                        )}
                      </td>

                      <td className="px-4 py-3">
                        <span
                          className={[
                            "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold",
                            hasDiscount ? "bg-[#fff1c9] text-[#7d5a19]" : "bg-[#f1e7de] text-[#6d5a53]",
                          ].join(" ")}
                        >
                          {hasDiscount ? "Discount Applicable" : "No Discount"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-2 text-xs text-[#7d6c64] sm:hidden">
            Tip: swipe left/right on the table to view all columns.
          </div>
        </div>
      </div>

      <style>{`
        .product-report-luxury {
          background:
            radial-gradient(circle at 6% 0%, rgba(212,175,55,.10), transparent 28%),
            radial-gradient(circle at 95% 92%, rgba(43,0,0,.04), transparent 30%),
            #F7EFE7;
        }

        .product-report-luxury input,
        .product-report-luxury select,
        .product-report-luxury button {
          font: inherit;
        }

        .product-report-luxury table {
          border-collapse: separate;
          border-spacing: 0;
        }

        .product-report-luxury th {
          border-bottom: 1px solid #d8c6b7;
        }

        .product-report-luxury ::selection {
          color: #fff7e7;
          background: #7a3131;
        }

        .product-report-luxury * {
          scrollbar-width: thin;
          scrollbar-color: rgba(143,111,56,.55) rgba(212,175,55,.07);
        }

        .product-report-luxury *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .product-report-luxury *::-webkit-scrollbar-track {
          background: rgba(212,175,55,.07);
        }

        .product-report-luxury *::-webkit-scrollbar-thumb {
          border: 2px solid transparent;
          border-radius: 999px;
          background: rgba(143,111,56,.55);
          background-clip: padding-box;
        }

        @media (max-width: 640px) {
          .product-report-luxury {
            padding: 10px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .product-report-luxury *,
          .product-report-luxury *::before,
          .product-report-luxury *::after {
            transition-duration: .01ms !important;
            animation-duration: .01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductReport;